const STATION_TZ = 'America/Fortaleza'
const WEEKDAY_MAP: Record<string, string> = {
  sun: 'DOM',
  mon: 'SEG',
  tue: 'TER',
  wed: 'QUA',
  thu: 'QUI',
  fri: 'SEX',
  sat: 'SÁB',
}

function stationParts(date = new Date()) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: STATION_TZ,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
}

export function formatSlot(startTime?: string, endTime?: string): string {
  const start = (startTime || '00:00').slice(0, 5)
  const end = (endTime || '00:00').slice(0, 5)
  const pretty = (value: string) => `${value.replace(':', 'h')}`
  return `${pretty(start)} às ${pretty(end)}`
}

export function nowMinutes(): number {
  const parts = stationParts()
  const hour = Number(parts.find((part) => part.type === 'hour')?.value || 0)
  const minute = Number(parts.find((part) => part.type === 'minute')?.value || 0)
  return hour * 60 + minute
}

export function toMinutes(value?: string): number {
  const [h, m] = (value || '00:00').split(':').map((part) => Number(part) || 0)
  return h * 60 + m
}

export function todayCode(): string {
  const weekday = (stationParts().find((part) => part.type === 'weekday')?.value || 'Mon').slice(0, 3).toLowerCase()
  return WEEKDAY_MAP[weekday] || 'SEG'
}

export function isOnAir(startTime?: string, endTime?: string, days?: string[]): boolean {
  const day = todayCode()
  if (days?.length && !days.includes(day)) return false
  const start = toMinutes(startTime)
  let end = toMinutes(endTime)
  let now = nowMinutes()
  if (end <= start) end += 24 * 60
  if (now < start && end > 24 * 60) now += 24 * 60
  return now >= start && now < end
}

export function getOnAirMember<T extends { active?: boolean; startTime?: string; endTime?: string; days?: string[] }>(team?: T[]): T | null {
  const live = (team || []).filter((item) => item?.active !== false && isOnAir(item.startTime, item.endTime, item.days))
  if (!live.length) return null
  live.sort((a, b) => toMinutes(b.startTime) - toMinutes(a.startTime))
  return live[0]
}

const WEEKDAY_CODES = ['SEG', 'TER', 'QUA', 'QUI', 'SEX']
const WEEKEND_CODES = ['SÁB', 'SAB', 'DOM']

function dayCodes(days?: string[]) {
  return (days || []).map((day) => (day || '').trim().toUpperCase())
}

export function hasWeekdaySlot(days?: string[]) {
  const codes = dayCodes(days)
  if (!codes.length) return true
  return codes.some((day) => WEEKDAY_CODES.includes(day))
}

export function hasWeekendSlot(days?: string[]) {
  const codes = dayCodes(days)
  if (!codes.length) return false
  return codes.some((day) => WEEKEND_CODES.includes(day))
}

function scheduleGroupRank(days?: string[]) {
  if (hasWeekdaySlot(days)) return 0
  if (hasWeekendSlot(days)) return 1
  return 2
}

export function compareBySchedule<T extends { startTime?: string; order?: number; name?: string; days?: string[] }>(a: T, b: T) {
  const group = scheduleGroupRank(a.days) - scheduleGroupRank(b.days)
  if (group !== 0) return group
  const time = toMinutes(a.startTime) - toMinutes(b.startTime)
  if (time !== 0) return time
  const order = (a.order || 0) - (b.order || 0)
  if (order !== 0) return order
  return (a.name || '').localeCompare(b.name || '', 'pt')
}

export function sortSchedule<T extends { startTime?: string; order?: number; name?: string; days?: string[] }>(items: T[]) {
  return [...items].sort(compareBySchedule)
}
